// What a build looks like: which pieces of the model, and where they sit.
//
// lib/items/customise.js says what somebody may choose and what it costs.
// This says what that choice IS, as pieces of public/models/buy_items.glb,
// so the thing in his world, the thing in the shop's preview and the thing
// on the receipt are one object. Imports nothing, like the catalogue, so
// the shop, the world and the tests all read the same file.
//
// The model is Sam's cut of a food pack (28 Sep), exported with the
// pack's own names. Pieces are found by those names with the Blender
// suffix taken off, so a re-export that renumbers ".001" to ".002" still
// lands. Every option in customise.js must resolve to pieces that exist in
// the model; test/parts.test.js reads the model and holds that.
//
// Positions are in MODEL units, x and z across the surface and y up from
// it, with every piece already standing on its base and centred (the
// viewer does that once when the model loads). `size(key)` hands back a
// piece's measured size so things can stack on each other without a
// number here that goes stale the day a piece is re-exported.

export const BUY_MODEL = '/models/buy_items.glb'

// Model units to world metres. The tray comes out about 1.4m long, which
// is what the tray at the burger place already is, so a bought meal and
// the town's own food read as the same size of thing.
export const BUY_SCALE = 0.4

/**
 * "Loaded Fries.001" -> "loaded_fries". Also drops the "_0" a
 * Sketchfab-sourced mesh carries after its Blender suffix, so the candle
 * ("Kaars.001_0", Dutch for candle) is plain "kaars".
 */
export function pieceKey(name) {
  return String(name || '')
    .replace(/(\.\d{3})?(_\d+)?$/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// Where food sits on each base, measured off the model: the tray's floor
// is inside its rim, the plate's a little above the table.
const SURFACE = { none: 0, tray: 0.05, plate: 0.035 }
const BASE_PIECE = { tray: 'tray', plate: 'plate' }

// How done, as a tint on the food and nothing else. Over 1 lifts it, which
// is what pale is; burnt takes most of the colour out.
export const DONENESS = {
  golden: null,
  pale: [1.2, 1.16, 1.02],
  burnt: [0.42, 0.3, 0.24],
}

// Drinks poured into the empty cup, as a flat disc of colour.
const POURED = {
  tea: '#9c6a36',
  chocolate: '#4a2a18',
  wrong: '#6f8a2e',
}

const DIP = {
  ketchup: 'ketchup_cup',
  mayo: 'mayonnaise_cup',
  mustard: 'mustard_cup',
  bbq: 'bbq_cup',
  cheese: 'cheese_cup',
  pickle: 'pickle',
}

// A portion of each fried thing, around its own centre.
const FRIED = {
  fries: [['loaded_fries', 0, 0, 0, 0]],
  rings: [
    ['onion_ring', 0, 0, 0, 0],
    ['onion_ring', 0.04, 0.085, 0.03, 0.9],
    ['onion_ring', -0.03, 0.17, 0.02, 2.1],
    ['onion_ring', 0.46, 0, 0.18, 0.4],
    ['onion_ring', 0.5, 0.085, 0.14, 1.7],
    ['onion_ring', 0.2, 0, -0.42, 2.9],
  ],
  sticks: [
    ['mozzarella_stick', -0.21, 0, 0, 0.12],
    ['mozzarella_stick', 0, 0, 0.04, -0.05],
    ['mozzarella_stick', 0.21, 0, -0.02, 0.18],
    ['mozzarella_stick_half', 0.08, 0.17, 0.1, 1.3],
  ],
  nuggets: [
    ['chicken_nuggets_1', 0, 0, 0, 0.3],
    ['chicken_nuggets_2', 0.3, 0, 0.1, 1.4],
    ['chicken_nuggets_3', -0.29, 0, 0.12, 2.6],
    ['chicken_nuggets_1', 0.04, 0, 0.33, 4.0],
    ['chicken_nuggets_2', 0.12, 0, -0.3, 5.1],
    ['chicken_nuggets_3', -0.2, 0, -0.26, 0.9],
  ],
}

const SWEET = {
  cookie: 'cookie_1',
  darkcookie: 'cookie_2',
  croissant: 'croissant',
  muffin: 'muffin_2',
  chocmuffin: 'muffin_1',
  waffle: 'waffle',
  roll: 'cinnamon_roll',
}

const DOUGHNUT = { glazed: 'donut_3', pink: 'donut_2', chocolate: 'donut_1' }
const TIN = { tuna: 'can_tuna', spam: 'can_spam' }

// Pieces from outside the food pack were modelled at their own sizes: the
// cans came in as tall as a coffee cup and the candle twice the height of
// a cinnamon roll. Each gets a factor that brings it to the pack's scale.
const OWN_SCALE = { can_tuna: 0.42, can_spam: 0.6, kaars: 0.34 }
const ICE = { vanilla: 'ice_cream_1', strawberry: 'ice_cream_2', chocolate: 'ice_cream_3', tub: 'ice_cream_cup_3' }
const CUP = { tea: 'coffee_cup_l', black: 'black_coffee_l', latte: 'coffee_l', chocolate: 'coffee_cup_l', wrong: 'coffee_cup_l' }

const part = (piece, x = 0, y = 0, z = 0, ry = 0, extra = {}) => ({ piece, x, y, z, ry, ...(OWN_SCALE[piece] ? { scale: OWN_SCALE[piece] } : {}), ...extra })
const many = (v) => (Array.isArray(v) ? v : [])

/**
 * The pieces for one build, each with where it sits.
 *
 * Returns [] for anything it does not know, which draws nothing: a gap
 * reads as nothing there yet, a wrong object reads as a broken shop.
 */
export function partsFor(itemId, spec = {}, size = () => ({ x: 0, y: 0, z: 0 })) {
  const s = spec || {}
  switch (itemId) {
    case 'chips': {
      const on = BASE_PIECE[s.on] ? s.on : 'none'
      const floor = SURFACE[on]
      const tint = DONENESS[s.done] || null
      // The food off to one side and the dips on the other, spaced for
      // what it is served on; on the ground they sit beside it.
      const at = { none: [0, [[0.72, -0.3], [0.72, 0.3]]], tray: [-0.62, [[0.95, -0.42], [0.95, 0.42]]], plate: [-0.22, [[0.5, -0.3], [0.5, 0.3]]] }[on]
      const out = []
      if (BASE_PIECE[on]) out.push(part(BASE_PIECE[on]))
      for (const [piece, x, y, z, ry] of FRIED[s.side] || FRIED.fries) {
        out.push(part(piece, at[0] + x, floor + y, z, ry, tint ? { tint, food: true } : { food: true }))
      }
      many(s.dip).slice(0, 2).forEach((d, i) => {
        const piece = DIP[d]
        if (!piece) return
        const [x, z] = at[1][i]
        out.push(part(piece, x, floor, z, i * 1.1))
      })
      return out
    }
    case 'thermos': {
      const cup = CUP[s.fill] || CUP.tea
      const h = size(cup).y || 1.14
      const out = [part(cup)]
      if (POURED[s.fill]) out.push(part(null, 0, h * 0.93, 0, 0, { disc: { radius: 0.34, color: POURED[s.fill] } }))
      if (s.lid === 'lid') out.push(part('hot_cup', 0, h - 0.04, 0))
      const withs = many(s.with)
      if (withs.includes('sugar')) out.push(part('sugar', 0.62, 0, 0.12, 0.5))
      if (withs.includes('stirrer')) out.push(part('coffee_stirrer', -0.6, 0, 0.05, -0.3))
      return out
    }
    case 'cake': {
      const on = s.on === 'plate' ? 'plate' : 'none'
      const floor = SURFACE[on]
      const base = SWEET[s.base] || SWEET.cookie
      const out = []
      if (on === 'plate') out.push(part('plate'))
      out.push(part(base, 0, floor, 0, 0.4))
      const top = floor + (size(base).y || 0.2) * 0.92
      const tops = many(s.top)
      // Both on it: the candle to the back and the pickle off to the front
      // side, or on a cookie the pickle stands in front of the candle and
      // hides it.
      const both = tops.includes('candle') && tops.includes('pickle')
      if (tops.includes('candle')) out.push(part('kaars', both ? -0.1 : 0, top, both ? -0.08 : 0, 0))
      if (tops.includes('pickle')) out.push(part('pickle', both ? 0.14 : 0.02, top, both ? 0.14 : 0, both ? 2.4 : 1.1))
      return out
    }
    case 'doughnut':
      return [part(DOUGHNUT[s.kind] || DOUGHNUT.glazed, 0, 0, 0, 0.6)]
    case 'icecream':
      return [part(ICE[s.kind] || ICE.vanilla)]
    case 'sardines':
      return [part(TIN[s.kind] || TIN.tuna, 0, 0, 0, 0.5)]
    default:
      return []
  }
}

/** Every piece a line can ever ask for, for the test that holds the model to it. */
export function piecesOf(itemId) {
  switch (itemId) {
    case 'chips': return [...new Set([...Object.values(BASE_PIECE), ...Object.values(DIP), ...Object.values(FRIED).flat().map((p) => p[0])])]
    case 'thermos': return [...new Set([...Object.values(CUP), 'hot_cup', 'sugar', 'coffee_stirrer'])]
    case 'cake': return [...new Set([...Object.values(SWEET), 'plate', 'pickle', 'kaars'])]
    case 'doughnut': return Object.values(DOUGHNUT)
    case 'icecream': return Object.values(ICE)
    case 'sardines': return Object.values(TIN)
    default: return []
  }
}

/** Items that can be drawn at all. Anything else stays off the map. */
export const DRAWN = ['chips', 'thermos', 'cake', 'doughnut', 'icecream', 'sardines']

/** How far across a build is, in world metres, so things set down can clear each other. */
export function footprintOf(itemId, spec = {}) {
  if (itemId === 'chips') return spec?.on === 'tray' ? 1.45 : 0.9
  if (itemId === 'thermos') return 0.6
  if (itemId === 'cake') return spec?.on === 'plate' ? 0.8 : 0.45
  return 0.4
}
